import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import { doc, getDoc, setDoc, collection, query, where, getDocs, serverTimestamp, onSnapshot } from "firebase/firestore";
import { auth, db, functions } from "./firebase";
import { httpsCallable } from "firebase/functions"; // STAFF-ACCOUNTS-1

const AuthContext = createContext(null);

const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes for store users

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [vendorUser, setVendorUser] = useState(null); // Firestore user doc
  const [vendor, setVendor] = useState(null); // Vendor/store doc
  const [allVendors, setAllVendors] = useState([]); // For multi-store owners
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(true);

  const lastActivityRef = useRef(Date.now());
  const logoutTimerRef = useRef(null);
  const vendorSubsRef = useRef([]);
  // GHOST-FIX-STORE-WEB #25: previously Sidebar + Topbar each opened
  // their own onSnapshot on the same query, doubling Firestore costs
  // and risking transient drift on store-switch. Lift the listener up
  // into auth context so both surfaces read the SAME number.
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const pendingSubRef = useRef(null);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    clearTimeout(logoutTimerRef.current);
    if (user) {
      logoutTimerRef.current = setTimeout(async () => {
        await signOut(auth);
        setUser(null); setVendorUser(null); setVendor(null); setAllVendors([]);
      }, SESSION_TIMEOUT_MS);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    const handler = () => resetTimer();
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      clearTimeout(logoutTimerRef.current);
    };
  }, [user, resetTimer]);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          // Get user document
          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
          if (userDoc.exists()) {
            const userData = { id: userDoc.id, ...userDoc.data() };
            // STAFF-ACCOUNTS-1: hard-block disabled accounts. Admin disables
            // staff by setting isActive:false (and that's the only revoke
            // path while there's no hard-delete CF for synthesized emails).
            // Accept either 'isActive' or legacy 'active'.
            const activeFlag = (userData.isActive !== undefined) ? userData.isActive : userData.active;
            if (activeFlag === false) {
              try { await signOut(auth); } catch (_) {}
              setUser(null); setVendorUser(null); setVendor(null); setAllVendors([]);
              setLoading(false); setInitializing(false);
              return;
            }
            setVendorUser(userData);

            // Get vendor(s) for this user. Multi-store owners are
            // assigned via the admin panel which writes the plural
            // `vendorIDs` array; legacy / single-store accounts use the
            // singular `vendorID` (string OR array). Union both so the
            // store switcher sees every assigned store.
            const vendorIdSet = new Set();
            const collect = (val) => {
              if (Array.isArray(val)) val.forEach((v) => v && vendorIdSet.add(String(v)));
              else if (val) vendorIdSet.add(String(val));
            };
            collect(userData.vendorIDs);
            collect(userData.vendorID);
            const vendorIds = Array.from(vendorIdSet);

            if (vendorIds.length > 0) {
              const vendors = [];
              for (const vid of vendorIds) {
                const vDoc = await getDoc(doc(db, "vendors", vid));
                if (vDoc.exists()) vendors.push({ id: vDoc.id, ...vDoc.data() });
              }
              setAllVendors(vendors);
              if (vendors.length > 0) setVendor(vendors[0]);
            }
            // GHOST-FIX-STORE-WEB #8: install per-vendor onSnapshot
            // listeners so `vendor` and `allVendors` rebuild whenever
            // `reststatus` / `storeStatus` / `wallet_amount` / commission
            // rate flip — Settings, admin panel, or another device.
            // Previously these were one-shot getDoc reads at login and
            // every Sidebar/Earnings/Dashboard widget reading them was
            // stale until next re-login.
            vendorSubsRef.current.forEach((u) => u && u());
            vendorSubsRef.current = vendorIds.map((vid) =>
              onSnapshot(doc(db, "vendors", vid), (snap) => {
                if (!snap.exists()) return;
                const fresh = { id: snap.id, ...snap.data() };
                setAllVendors((prev) => {
                  const next = prev.slice();
                  const idx = next.findIndex((v) => v.id === fresh.id);
                  if (idx >= 0) next[idx] = fresh; else next.push(fresh);
                  return next;
                });
                setVendor((cur) => (cur && cur.id === fresh.id ? fresh : cur));
              }),
            );

            // Update last login
            await setDoc(doc(db, "users", firebaseUser.uid), { lastOnlineTimestamp: serverTimestamp() }, { merge: true });
          }
        } catch (err) {
          console.error("Failed to fetch vendor data:", err);
        }
      } else {
        // GHOST-FIX-STORE-WEB #8: tear down any active vendor listeners
        // on logout so we don't leak Firestore listeners.
        vendorSubsRef.current.forEach((u) => u && u());
        vendorSubsRef.current = [];
        setUser(null); setVendorUser(null); setVendor(null); setAllVendors([]);
      }
      setLoading(false);
      setInitializing(false);
    });
    return () => {
      unsubscribe();
      vendorSubsRef.current.forEach((u) => u && u());
      vendorSubsRef.current = [];
    };
  }, []);

  // STAFF-ACCOUNTS-1: accept either an email or a username. If the
  // identifier has no '@' we call the resolveUsername CF (same callable
  // the store Flutter app uses) to fetch the real auth email, then sign
  // in normally. Owners with real emails skip the round-trip entirely.
  const login = async (identifier, password, rememberMe = false) => {
    await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
    let loginEmail = String(identifier || "").trim();
    if (loginEmail && !loginEmail.includes("@")) {
      try {
        const res = await httpsCallable(functions, "resolveUsername")({ username: loginEmail.toLowerCase() });
        const resolved = res && res.data && res.data.email;
        if (!resolved) {
          const err = new Error("Invalid username or password");
          err.code = "auth/user-not-found";
          throw err;
        }
        loginEmail = String(resolved).trim();
      } catch (e) {
        // Translate CF errors into auth-style codes so the existing Login
        // page error mapping keeps working.
        const msg = (e && e.message) || "";
        const wrapped = new Error("Invalid username or password");
        wrapped.code = (e && e.code === "functions/not-found") ? "auth/user-not-found" : (e && e.code) || "auth/invalid-credential";
        wrapped.cause = msg;
        throw wrapped;
      }
    }
    return await signInWithEmailAndPassword(auth, loginEmail, password);
  };

  const logout = async () => {
    clearTimeout(logoutTimerRef.current);
    vendorSubsRef.current.forEach((u) => u && u());
    vendorSubsRef.current = [];
    await signOut(auth);
    setUser(null); setVendorUser(null); setVendor(null); setAllVendors([]);
  };

  const switchVendor = (vendorId) => {
    const v = allVendors.find((v) => v.id === vendorId);
    if (v) setVendor(v);
  };

  // Shared pending-orders listener (one per active vendor).
  useEffect(() => {
    pendingSubRef.current?.();
    pendingSubRef.current = null;
    if (!vendor?.id) { setPendingOrderCount(0); return; }
    const q = query(
      collection(db, "vendor_orders"),
      where("vendorID", "==", vendor.id),
      where("status", "==", "Order Placed"),
    );
    pendingSubRef.current = onSnapshot(
      q,
      (snap) => setPendingOrderCount(snap.size),
      (err) => console.error("[auth] pending-orders snapshot:", err),
    );
    return () => { pendingSubRef.current?.(); pendingSubRef.current = null; };
  }, [vendor?.id]);

  const value = {
    user, vendorUser, vendor, allVendors, initializing, loading,
    login, logout, switchVendor,
    isAuthenticated: !!user && !!vendor,
    vendorId: vendor?.id || null,
    vendorName: vendor?.title || vendor?.name || "Store",
    pendingOrderCount,
  };

  if (initializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex flex-col items-center justify-center">
        <div className="text-center space-y-6">
          <h1 className="text-4xl font-black tracking-tight">
            <span className="text-amigo-orange">ami</span><span className="text-amigo-teal">Go</span>
          </h1>
          <p className="text-white/30 text-xs uppercase tracking-widest">Store Dashboard</p>
          <div className="relative mx-auto w-14 h-14">
            <div className="absolute inset-0 border-3 border-white/10 rounded-full" />
            <div className="absolute inset-0 border-3 border-transparent border-t-amigo-orange rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
